
/* ----------------------------------------------------------------------
   Desktop nav flyouts

   Sizes every open panel to the room it actually has, and places the third
   level in viewport coordinates so the scrolling second level cannot clip
   it. See the matching CSS block for the reasoning.

   The theme keeps ownership of open and close. Each level is a <details>
   that Empire toggles; this only measures and positions what Empire has
   opened, so if the script never runs the nav behaves as it does today.
---------------------------------------------------------------------- */
(function () {
  var header = document.querySelector('[data-site-header]');
  if (!header || header.dataset.tcgNavFlyouts === 'true') return;

  var nav = header.querySelector('.site-navigation');
  if (!nav) return;
  header.dataset.tcgNavFlyouts = 'true';

  var EDGE = 12;   // breathing room against the viewport edges
  var mq = window.matchMedia('(min-width: 1025px)');
  var frame = null;

  function desktop() { return mq.matches; }

  /* Empire's shape is li > details > ul. The panel is never a direct child
     of the item, which is the whole reason a naive lookup finds nothing. */
  function panelOf(item) {
    if (!item || item.tagName !== 'LI') return null;
    var details = item.querySelector(':scope > details');
    var panel = details ? details.querySelector(':scope > ul') : item.querySelector(':scope > ul');
    return panel || null;
  }

  /* The theme already labels the level: navmenu-depth-2, -3, ... */
  function levelOf(panel) {
    var match = /navmenu-depth-(\d+)/.exec(String(panel.className || ''));
    return match ? parseInt(match[1], 10) : 2;
  }

  function openPanels() {
    var out = [];
    nav.querySelectorAll('details[open]').forEach(function (details) {
      var panel = details.querySelector(':scope > ul');
      if (panel) out.push(panel);
    });
    return out;
  }

  /* These panels are box-sizing: content-box, so max-height caps the CONTENT
     box and padding lands on top of it. Measure that overhang rather than
     guessing, or a padded panel overshoots the bottom of the screen. */
  function boxExtra(panel) {
    var cs = getComputedStyle(panel);
    if (cs.boxSizing !== 'content-box') return 0;
    return (parseFloat(cs.paddingTop) || 0) + (parseFloat(cs.paddingBottom) || 0) +
           (parseFloat(cs.borderTopWidth) || 0) + (parseFloat(cs.borderBottomWidth) || 0);
  }

  /* Size of a panel that is still closed: shown invisibly for one
     measurement, then put back exactly as it was. */
  function measure(panel) {
    if (panel.getClientRects().length) {
      return { w: panel.offsetWidth, h: panel.scrollHeight };
    }
    var prev = panel.style.cssText;
    panel.style.setProperty('display', 'block', 'important');
    panel.style.setProperty('visibility', 'hidden', 'important');
    panel.style.setProperty('opacity', '0', 'important');
    panel.style.setProperty('height', 'auto', 'important');
    panel.style.setProperty('max-height', 'none', 'important');
    var size = { w: panel.offsetWidth, h: panel.scrollHeight };
    panel.style.cssText = prev;
    return size;
  }

  /* ---- scroll affordance ------------------------------------------------ */
  function syncEnd(panel) {
    if (panel.scrollTop + panel.clientHeight >= panel.scrollHeight - 6) panel.setAttribute('data-tcg-end', '');
    else panel.removeAttribute('data-tcg-end');
  }

  function markScroll(panel) {
    if (panel.scrollHeight - panel.clientHeight > 4) panel.setAttribute('data-tcg-scrolls', '');
    else panel.removeAttribute('data-tcg-scrolls');
    syncEnd(panel);
  }

  function bindScroll(panel) {
    if (panel.hasAttribute('data-tcg-sub')) return;
    panel.setAttribute('data-tcg-sub', '');
    panel.addEventListener('scroll', function () {
      syncEnd(panel);
      // A level-3 flyout is anchored to a row inside this panel, so scrolling
      // the panel has to carry it along.
      scheduleRefresh();
    }, { passive: true });
  }

  /* ---- placement -------------------------------------------------------- */

  /* The highest a flyout may reach.

     Level 3 slides up to keep its last row on screen, and used to stop at
     EDGE - 12px from the top of the VIEWPORT. The header is ~246px tall and
     the flyout sits at z-index 1103 above it, so any list tall enough to need
     the slide (Scarlet & Violet, 25 sets) rode straight up over the search
     bar, the logo and the utility links.

     The header is the real ceiling, not the viewport. Measured live rather
     than assumed: it is sticky, so its bottom moves, and once it has scrolled
     away (bottom <= 0) the whole viewport is fair game again. */
  function topBoundary() {
    var limit = EDGE;
    if (header) {
      var hr = header.getBoundingClientRect();
      if (hr.bottom > 0 && hr.top < window.innerHeight) {
        limit = Math.max(limit, hr.bottom + 8);
      }
    }
    return limit;
  }

  function sizeLevel2(panel) {
    var top;
    if (panel.getClientRects().length) {
      top = panel.getBoundingClientRect().top;
    } else {
      var owner = panel.parentElement && panel.parentElement.closest('li');
      top = owner ? owner.getBoundingClientRect().bottom : 0;
    }
    var avail = window.innerHeight - top - EDGE - boxExtra(panel);
    panel.style.setProperty('--tcg-sub-maxh', Math.max(180, Math.round(avail)) + 'px');
    markScroll(panel);
  }

  function placeLevel3(panel) {
    var item = panel.parentElement && panel.parentElement.closest('li');
    if (!item) return;

    var ir = item.getBoundingClientRect();
    var vw = window.innerWidth;
    var vh = window.innerHeight;
    var extra = boxExtra(panel);
    /* Room between the header and the bottom edge - not the whole viewport,
       which is what let a tall list grow past the header and then slide into
       it. Level 2 has always measured from its own top for this reason; this
       is the same rule applied one level down. */
    var minTop = topBoundary();
    var avail = vh - minTop - EDGE - extra;
    panel.style.setProperty('--tcg-sub-maxh', Math.max(180, Math.round(avail)) + 'px');

    var size = measure(panel);
    var w = size.w || 240;
    var h = Math.min(size.h + extra, vh - minTop - EDGE);

    // Sideways: out to the right as the theme intends, flipping to the left
    // when there is no room for it there.
    var left = ir.right - 1;
    if (left + w > vw - EDGE) {
      var flipped = ir.left - w + 1;
      left = flipped >= EDGE ? flipped : Math.max(EDGE, vw - EDGE - w);
    }

    // Vertically: level with its row as before, then slid up as far as it
    // takes to keep the last set on screen. This is the part that was missing.
    var top = ir.top - 15;
    if (top + h > vh - EDGE) top = vh - EDGE - h;
    if (top < minTop) top = minTop;

    panel.style.setProperty('--tcg-sub-left', Math.round(left) + 'px');
    panel.style.setProperty('--tcg-sub-top', Math.round(top) + 'px');

    /* `fixed` resolves against the viewport unless an ancestor is transformed.
       The header is sticky today and sticky does not do that - but a theme
       update could, so measure where the panel actually landed and correct by
       the difference rather than trusting it. */
    if (panel.getClientRects().length) {
      var pr = panel.getBoundingClientRect();
      var dx = left - pr.left;
      var dy = top - pr.top;
      if (Math.abs(dx) > 0.5 || Math.abs(dy) > 0.5) {
        panel.style.setProperty('--tcg-sub-left', Math.round(left + dx) + 'px');
        panel.style.setProperty('--tcg-sub-top', Math.round(top + dy) + 'px');
      }
    }
    markScroll(panel);
  }

  function place(panel) {
    if (!panel || !desktop()) return;
    if (levelOf(panel) >= 3) placeLevel3(panel);
    else sizeLevel2(panel);
  }

  function scheduleRefresh() {
    if (frame) return;
    frame = requestAnimationFrame(function () {
      frame = null;
      if (desktop()) openPanels().forEach(place);
    });
  }

  /* ---- wiring ----------------------------------------------------------- */

  /* Pre-place on approach. The details `toggle` event is queued as a task, so
     on its own it can leave one frame where a level-3 panel is still parked
     inside the scrollport that would clip it. pointerover runs first. */
  nav.addEventListener('pointerover', function (e) {
    if (!desktop() || !e.target || !e.target.closest) return;
    var item = e.target.closest('li');
    if (!item || !nav.contains(item)) return;
    var panel = panelOf(item);
    if (!panel || panel.getAttribute('data-tcg-armed') === '1') return;
    panel.setAttribute('data-tcg-armed', '1');
    bindScroll(panel);
    place(panel);
  });

  /* Authoritative: fires whenever the theme opens or closes a level, however
     it was triggered - hover, click, keyboard. Non-bubbling, hence capture. */
  nav.addEventListener('toggle', function (e) {
    var details = e.target;
    if (!details || details.tagName !== 'DETAILS' || !desktop()) return;
    var panel = details.querySelector(':scope > ul');
    if (!panel) return;

    if (!details.open) {
      panel.removeAttribute('data-tcg-armed');
      panel.removeAttribute('data-tcg-scrolls');
      panel.removeAttribute('data-tcg-end');
      panel.scrollTop = 0;
      return;
    }

    bindScroll(panel);
    place(panel);
    // Re-measure once the theme's open animation has run its height.
    requestAnimationFrame(function () { place(panel); });
    setTimeout(function () { place(panel); }, 260);
  }, true);

  /* A fixed panel is pinned to the viewport, so anything that moves its
     anchor has to move it too. */
  window.addEventListener('scroll', scheduleRefresh, { passive: true });
  window.addEventListener('resize', scheduleRefresh);

  /* Escape closes the open levels, innermost first. */
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    var open = nav.querySelectorAll('details[open]');
    for (var i = open.length - 1; i >= 0; i--) open[i].open = false;
  });
}());
